import type { WidgetData, WidgetType } from '@elara/domain/models/schemas/widget-schemas';
import type { ToolCallStatus } from '@elara/domain/models/ToolCallSpec';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { BadgeVariant } from '@elara/client-core/ui-contracts/badge-variant';
import type { AnyWidgetConfig, WidgetConfig, WidgetInputProps, WidgetReactProps } from '@elara/domain/models/schemas/widget-spec';

import { z } from 'zod';

import { assert, errorMessage } from '@elara/core/errors';
import { validateWidgetData } from '@elara/domain/models/schemas/widget-schemas';
import { neutralWidgetSpecMap, preprocessWidgetData } from '@elara/domain/models/schemas/widget-spec';
import { ToolCallSpec } from '@elara/domain/models/ToolCallSpec';

import { createClientModel } from '#firebase/createClientModel.js';
import { formatShortToolError } from '#lib/errors.js';

function toMillis(value: unknown): number | undefined {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    return (value.toDate() as Date).getTime();
  }
  return;
}

function normalizeError(error: unknown): string | undefined {
  if (error === undefined || error === null) return;
  if (typeof error === 'string') return error || undefined;
  if (error instanceof Error) return error.message || undefined;
  return String(error) || undefined;
}

export const TOOL_CALL_STUCK_SECS = 15 * 60; // 15 minutes

type WidgetParseResult = { success: true; data: WidgetReactProps } | { success: false; error: z.ZodError };

export function createToolCallModel(firestore: FirestoreContainer) {
  const model = createClientModel(firestore, ToolCallSpec, () => ({
    get error(): string | undefined {
      return normalizeError(this.response?.error);
    },
    get shortErrorMessage(): string | undefined {
      return formatShortToolError(this.error);
    },
    get input(): Record<string, unknown> | undefined {
      return this.request?.input;
    },
    get output(): unknown {
      return this.response?.output;
    },
    get startTime(): number | undefined {
      return toMillis(this.startedAt);
    },
    get endTime(): number | undefined {
      return toMillis(this.finishedAt);
    },
    get startedAt(): Date | undefined {
      return this.request?.timestamp;
    },
    get durationMs(): number {
      return this.response?.duration ?? 0;
    },
    get durationString(): string | undefined {
      const ms = this.response?.duration;
      if (!ms) return undefined;
      return `${(ms / 1000).toFixed(2)}s`;
    },
    get isActive(): boolean {
      return (this.status === 'pending' || this.status === 'executing') && !this.isStuck;
    },
    get hasFailed(): boolean {
      return this.status === 'failed';
    },
    get finishedAt(): Date | undefined {
      return this.response?.timestamp;
    },
    get timeSinceStartS(): number | undefined {
      const start = this.startedAt?.getTime();
      if (!start) return;
      return (Date.now() - start) / 1000;
    },
    get isStuck(): boolean {
      return (
        (this.status === 'pending' || this.status === 'executing') &&
        !!this.timeSinceStartS &&
        this.timeSinceStartS > TOOL_CALL_STUCK_SECS
      );
    },
    get extendedStatus(): ToolCallStatus | 'stuck' {
      if (this.isStuck) return 'stuck';
      if (this.widgetError) return 'failed';
      return this.status;
    },
    get wallTimeMs(): number | undefined {
      const start = this.startedAt?.getTime();
      const end = this.finishedAt?.getTime();
      if (start && end) return end - start;
      else return;
    },
    get wallTimeString(): string {
      const ms = this.wallTimeMs;
      if (!ms) return '';
      return `${(ms / 1000).toFixed(2)}s`;
    },
    get humanToolName(): string {
      return this.toolName;
    },
    get humanCallType(): string {
      return this.isWidget ? 'widget' : this.callType;
    },
    get badgeVariant(): BadgeVariant {
      switch (this.extendedStatus) {
        case 'pending': {
          return 'outline';
        }
        case 'executing': {
          return 'secondary';
        }
        case 'completed': {
          return 'default';
        }
        case 'failed': {
          return 'destructive';
        }
        case 'stuck': {
          return 'destructive';
        }
        default: {
          const _: never = this.extendedStatus;
          return 'outline';
        }
      }
    },
    get isWidget(): boolean {
      return this.toolName.endsWith('Widget');
    },
    get widgetType(): WidgetType | undefined {
      if (!this.isWidget) return;
      return this.toolName as WidgetType | undefined;
    },
    get widgetRawData(): WidgetData<WidgetType> | undefined {
      if (!this.isWidget) return;
      return this.input?.data as WidgetData<WidgetType>;
    },
    get widgetConfig(): WidgetConfig<WidgetInputProps, WidgetReactProps> | undefined {
      if (!this.isWidget) return;
      // Component-free spec map: ToolCallModel only needs the data transform (makeReactProps),
      // not the platform component, so it depends on the neutral map rather than the web #ui registry.
      return neutralWidgetSpecMap[this.widgetType!] as AnyWidgetConfig;
    },
    get widgetError(): unknown | undefined {
      if (!this.isWidget) return;
      if (this.error) return this.error;
      const widgetProps = this.widgetProps;
      if (!widgetProps || widgetProps.success) return;
      return widgetProps.error;
    },
    get widgetProps(): WidgetParseResult | undefined {
      if (!this.isWidget) return;
      if (!this.widgetRawData) return;

      const normalizedData = preprocessWidgetData(this.widgetRawData);
      const validationResult = validateWidgetData(this.widgetType!, normalizedData);
      if (!validationResult.success) return validationResult;

      const widgetConfig = this.widgetConfig;
      if (!widgetConfig) {
        return {
          success: false,
          error: new z.ZodError([{ code: 'custom', message: 'Widget config not found', path: [] }]),
        };
      }

      try {
        const data = widgetConfig.makeReactProps(validationResult.data as WidgetInputProps);
        assert(data, 'Widget transformProps returned undefined');
        return { success: true, data };
      } catch (error) {
        return {
          success: false,
          error: new z.ZodError([{ code: 'custom', message: `Widget error: ${errorMessage(error)}`, path: [] }]),
        };
      }
    },
  }));

  return model;
}

export type ToolCallModel = ReturnType<typeof createToolCallModel>;
export type ToolCall = ClientInstanceOf<ToolCallModel>;
